/**
 * Tasks API service - handles task API calls
 */

import { apiClient } from './client';

// Types
export interface Label {
  id: string;
  projectId: string;
  name: string;
  color: string;
  description?: string;
}

export interface Subtask {
  id: string;
  taskId: string;
  title: string;
  isCompleted: boolean;
  position: number;
  completedAt?: string;
  completedById?: string;
  createdAt: string;
}

export interface TaskDependency {
  id: string;
  blockingTaskId: string;
  blockedTaskId: string;
  dependencyType: 'blocks' | 'blocked_by' | 'related';
  createdAt: string;
}

export interface CustomFieldValue {
  fieldId: string;
  fieldName: string;
  fieldType: string;
  value: any;
}

export interface TaskAssignee {
  id: string;
  displayName: string;
  avatarUrl?: string;
  assigneeType: 'user' | 'contact';
  email?: string; // For contacts
  company?: string; // For contacts
}

export interface Task {
  id: string;
  boardId: string;
  columnId: string;
  taskKey: string;
  taskNumber: number;
  title: string;
  description?: string;
  taskType: string;
  priority: 'urgent' | 'high' | 'medium' | 'low' | 'none';
  position: number;
  assigneeId?: string;
  assigneeContactId?: string;
  assigneeType?: 'user' | 'contact';
  reporterId?: string;
  parentTaskId?: string;
  estimatedMinutes?: number;
  actualMinutes: number;
  dueDate?: string;
  startDate?: string;
  completedAt?: string;
  labels: Label[];
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  // Enriched fields for card display
  assignee?: TaskAssignee;
  subtaskCount?: number;
  subtaskCompletedCount?: number;
  attachmentCount?: number;
  // Source meeting info (if task was extracted from a meeting)
  sourceMeetingId?: string;
  sourceMeeting?: {
    id: string;
    title: string;
  };
  // Source email info (if task was extracted from an email)
  sourceEmailId?: string;
  sourceEmail?: {
    id: string;
    subject: string;
  };
  // Source language (ISO code of the original content, e.g. 'fr', 'en')
  sourceLanguage?: string;
}

export interface TaskDetail extends Task {
  subtasks: Subtask[];
  dependencies: TaskDependency[];
  customFieldValues: CustomFieldValue[];
  assignee?: TaskAssignee;
  reporter?: {
    id: string;
    displayName: string;
    avatarUrl?: string;
  };
}

export interface TaskListResponse {
  items: Task[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface TaskFilterParams {
  boardId?: string;
  columnId?: string;
  assigneeId?: string;
  priority?: string;
  taskType?: string;
  isCompleted?: boolean;
  isOverdue?: boolean;
  search?: string;
}

export interface CreateTaskRequest {
  boardId: string;
  columnId?: string;
  title: string;
  description?: string;
  taskType?: string;
  priority?: 'urgent' | 'high' | 'medium' | 'low' | 'none';
  assigneeId?: string;
  dueDate?: string;
  startDate?: string;
  estimatedMinutes?: number;
  parentTaskId?: string;
  labelIds?: string[];
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  taskType?: string;
  priority?: 'urgent' | 'high' | 'medium' | 'low' | 'none';
  columnId?: string;
  assigneeId?: string | null;
  assigneeContactId?: string | null;
  dueDate?: string | null;
  startDate?: string | null;
  estimatedMinutes?: number | null;
}

export interface MoveTaskRequest {
  columnId?: string;
  boardId?: string;
  position?: number;
}

export interface BulkMoveRequest {
  taskIds: string[];
  columnId: string;
}

export interface MoveToProjectRequest {
  targetBoardId: string;
  targetColumnId: string;
  targetPosition: number;
}

export interface MoveToProjectResponse {
  task: Task;
  previousTaskKey: string;
  previousBoardId: string;
  previousProjectId: string;
  labelsMapped: number;
  labelsDropped: number;
}

export interface CreateSubtaskRequest {
  title: string;
  position?: number;
}

export interface UpdateSubtaskRequest {
  title?: string;
  isCompleted?: boolean;
}

export interface CreateLabelRequest {
  projectId: string;
  name: string;
  color: string;
  description?: string;
}

export interface UpdateLabelRequest {
  name?: string;
  color?: string;
  description?: string;
}

export interface CreateDependencyRequest {
  blockingTaskId: string;
  blockedTaskId: string;
  dependencyType?: 'blocks' | 'blocked_by' | 'related';
}

export interface SetCustomFieldRequest {
  fieldId: string;
  value: any;
}

export interface EmailTaskRequest {
  recipients: string[];
  message?: string;
  include_comments: boolean;
}

/**
 * Tasks API methods
 */
export const tasksApi = {
  // =====================
  // Task CRUD
  // =====================

  /**
   * List tasks with filtering
   */
  async list(
    filters: TaskFilterParams = {},
    page = 1,
    pageSize = 50
  ): Promise<TaskListResponse> {
    const params: any = { page, page_size: pageSize };
    if (filters.boardId) params.board_id = filters.boardId;
    if (filters.columnId) params.column_id = filters.columnId;
    if (filters.assigneeId) params.assignee_id = filters.assigneeId;
    if (filters.priority) params.priority = filters.priority;
    if (filters.taskType) params.task_type = filters.taskType;
    if (filters.isCompleted !== undefined) params.is_completed = filters.isCompleted;
    if (filters.isOverdue !== undefined) params.is_overdue = filters.isOverdue;
    if (filters.search) params.search = filters.search;

    const response = await apiClient.get<any>('/tasks', { params });
    return {
      items: response.data.items.map(transformTask),
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.pageSize ?? response.data.page_size,
      hasMore: response.data.hasMore ?? response.data.has_more,
    };
  },

  /**
   * Get task by ID
   */
  async get(taskId: string): Promise<TaskDetail> {
    const response = await apiClient.get<any>(`/tasks/${taskId}`);
    return transformTaskDetail(response.data);
  },

  /**
   * Get task by key (e.g., PROJ-123)
   */
  async getByKey(taskKey: string): Promise<TaskDetail> {
    const response = await apiClient.get<any>(`/tasks/key/${taskKey}`);
    return transformTaskDetail(response.data);
  },

  /**
   * Create a new task
   */
  async create(data: CreateTaskRequest): Promise<Task> {
    const response = await apiClient.post<any>('/tasks', {
      board_id: data.boardId,
      column_id: data.columnId,
      title: data.title,
      description: data.description,
      task_type: data.taskType || 'task',
      priority: data.priority || 'none',
      assignee_id: data.assigneeId,
      due_date: data.dueDate,
      start_date: data.startDate,
      estimated_time: data.estimatedMinutes,
      parent_task_id: data.parentTaskId,
      label_ids: data.labelIds,
    });
    return transformTask(response.data);
  },

  /**
   * Update a task
   */
  async update(taskId: string, data: UpdateTaskRequest): Promise<Task> {
    const payload: any = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.taskType !== undefined) payload.task_type = data.taskType;
    if (data.priority !== undefined) payload.priority = data.priority;
    if (data.columnId !== undefined) payload.column_id = data.columnId;
    if (data.assigneeId !== undefined) payload.assignee_id = data.assigneeId;
    if (data.assigneeContactId !== undefined) payload.assignee_contact_id = data.assigneeContactId;
    if (data.dueDate !== undefined) payload.due_date = data.dueDate;
    if (data.startDate !== undefined) payload.start_date = data.startDate;
    if (data.estimatedMinutes !== undefined)
      payload.estimated_time = data.estimatedMinutes;

    const response = await apiClient.patch<any>(`/tasks/${taskId}`, payload);
    return transformTask(response.data);
  },

  /**
   * Delete a task
   */
  async delete(taskId: string): Promise<void> {
    await apiClient.delete(`/tasks/${taskId}`);
  },

  // =====================
  // Task Actions
  // =====================

  /**
   * Move a task to a new column/position
   */
  async move(taskId: string, data: MoveTaskRequest): Promise<Task> {
    const payload: any = {};
    if (data.columnId !== undefined) payload.column_id = data.columnId;
    if (data.boardId !== undefined) payload.board_id = data.boardId;
    if (data.position !== undefined) payload.position = data.position;

    const response = await apiClient.post<any>(`/tasks/${taskId}/move`, payload);
    return transformTask(response.data);
  },

  /**
   * Move a task to a different project/board/column.
   * This handles cross-project moves where the task gets a new task_key.
   * Labels are mapped by name to the destination project.
   */
  async moveToProject(
    taskId: string,
    data: MoveToProjectRequest
  ): Promise<MoveToProjectResponse> {
    const response = await apiClient.post<any>(`/tasks/${taskId}/move-to-project`, {
      target_board_id: data.targetBoardId,
      target_column_id: data.targetColumnId,
      target_position: data.targetPosition,
    });
    return {
      task: transformTask(response.data.task),
      previousTaskKey: response.data.previous_task_key,
      previousBoardId: response.data.previous_board_id,
      previousProjectId: response.data.previous_project_id,
      labelsMapped: response.data.labels_mapped,
      labelsDropped: response.data.labels_dropped,
    };
  },

  /**
   * Bulk move multiple tasks
   */
  async bulkMove(data: BulkMoveRequest): Promise<void> {
    await apiClient.post('/tasks/bulk-move', {
      task_ids: data.taskIds,
      column_id: data.columnId,
    });
  },

  /**
   * Mark task as completed
   */
  async complete(taskId: string): Promise<Task> {
    const response = await apiClient.post<any>(`/tasks/${taskId}/complete`);
    return transformTask(response.data);
  },

  /**
   * Mark task as not completed
   */
  async uncomplete(taskId: string): Promise<Task> {
    const response = await apiClient.post<any>(`/tasks/${taskId}/uncomplete`);
    return transformTask(response.data);
  },

  /**
   * Log time spent on a task
   */
  async logTime(taskId: string, minutes: number): Promise<Task> {
    const response = await apiClient.post<any>(`/tasks/${taskId}/log-time`, {
      minutes,
    });
    return transformTask(response.data);
  },

  // =====================
  // Labels
  // =====================

  /**
   * Add a label to a task
   */
  async addLabel(taskId: string, labelId: string): Promise<Task> {
    const response = await apiClient.post<any>(
      `/tasks/${taskId}/labels/${labelId}`
    );
    return transformTask(response.data);
  },

  /**
   * Remove a label from a task
   */
  async removeLabel(taskId: string, labelId: string): Promise<Task> {
    const response = await apiClient.delete<any>(
      `/tasks/${taskId}/labels/${labelId}`
    );
    return transformTask(response.data);
  },

  // =====================
  // Dependencies
  // =====================

  /**
   * Add a dependency between tasks
   */
  async addDependency(
    taskId: string,
    data: CreateDependencyRequest
  ): Promise<TaskDependency> {
    const response = await apiClient.post<any>(`/tasks/${taskId}/dependencies`, {
      blocking_task_id: data.blockingTaskId,
      blocked_task_id: data.blockedTaskId,
      dependency_type: data.dependencyType || 'blocks',
    });
    return transformDependency(response.data);
  },

  /**
   * Remove a dependency
   */
  async removeDependency(dependencyId: string): Promise<void> {
    await apiClient.delete(`/tasks/dependencies/${dependencyId}`);
  },

  // =====================
  // Custom Fields
  // =====================

  /**
   * Set a custom field value
   */
  async setCustomField(taskId: string, data: SetCustomFieldRequest): Promise<void> {
    await apiClient.post(`/tasks/${taskId}/custom-fields`, {
      field_id: data.fieldId,
      value: data.value,
    });
  },

  /**
   * Email task to recipients
   */
  async emailTask(taskId: string, data: EmailTaskRequest): Promise<void> {
    await apiClient.post(`/tasks/${taskId}/email`, data);
  },
};

/**
 * Subtasks API methods
 */
export const subtasksApi = {
  /**
   * List subtasks for a task
   */
  async list(taskId: string): Promise<Subtask[]> {
    const response = await apiClient.get<any[]>(
      `/tasks/${taskId}/subtasks`
    );
    return response.data.map(transformSubtask);
  },

  /**
   * Create a subtask
   */
  async create(taskId: string, data: CreateSubtaskRequest): Promise<Subtask> {
    const response = await apiClient.post<any>(`/tasks/${taskId}/subtasks`, {
      title: data.title,
      position: data.position,
    });
    return transformSubtask(response.data);
  },

  /**
   * Get a subtask
   */
  async get(taskId: string, subtaskId: string): Promise<Subtask> {
    const response = await apiClient.get<any>(
      `/tasks/${taskId}/subtasks/${subtaskId}`
    );
    return transformSubtask(response.data);
  },

  /**
   * Update a subtask
   */
  async update(
    taskId: string,
    subtaskId: string,
    data: UpdateSubtaskRequest
  ): Promise<Subtask> {
    const payload: any = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.isCompleted !== undefined) payload.is_completed = data.isCompleted;

    const response = await apiClient.patch<any>(
      `/tasks/${taskId}/subtasks/${subtaskId}`,
      payload
    );
    return transformSubtask(response.data);
  },

  /**
   * Delete a subtask
   */
  async delete(taskId: string, subtaskId: string): Promise<void> {
    await apiClient.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
  },

  /**
   * Mark subtask as completed
   */
  async complete(taskId: string, subtaskId: string): Promise<Subtask> {
    const response = await apiClient.post<any>(
      `/tasks/${taskId}/subtasks/${subtaskId}/complete`
    );
    return transformSubtask(response.data);
  },

  /**
   * Mark subtask as not completed
   */
  async uncomplete(taskId: string, subtaskId: string): Promise<Subtask> {
    const response = await apiClient.post<any>(
      `/tasks/${taskId}/subtasks/${subtaskId}/uncomplete`
    );
    return transformSubtask(response.data);
  },

  /**
   * Reorder subtasks
   */
  async reorder(taskId: string, subtaskIds: string[]): Promise<Subtask[]> {
    const response = await apiClient.post<any[]>(
      `/tasks/${taskId}/subtasks/reorder`,
      subtaskIds
    );
    return response.data.map(transformSubtask);
  },

  /**
   * Get subtask progress
   */
  async getProgress(taskId: string): Promise<{
    completed: number;
    total: number;
    percentage: number;
  }> {
    const response = await apiClient.get<any>(
      `/tasks/${taskId}/subtasks/progress`
    );
    return {
      completed: response.data.completed,
      total: response.data.total,
      percentage: response.data.percentage,
    };
  },
};

/**
 * Labels API methods
 */
export const labelsApi = {
  /**
   * List labels for a project
   */
  async list(projectId: string): Promise<Label[]> {
    const response = await apiClient.get<any[]>('/labels', {
      params: { project_id: projectId },
    });
    return response.data.map(transformLabel);
  },

  /**
   * Search labels by name
   */
  async search(projectId: string, query: string): Promise<Label[]> {
    const response = await apiClient.get<any[]>('/labels/search', {
      params: { project_id: projectId, q: query },
    });
    return response.data.map(transformLabel);
  },

  /**
   * Get a label by ID
   */
  async get(labelId: string): Promise<Label> {
    const response = await apiClient.get<any>(`/labels/${labelId}`);
    return transformLabel(response.data);
  },

  /**
   * Create a new label
   */
  async create(data: CreateLabelRequest): Promise<Label> {
    const response = await apiClient.post<any>('/labels', {
      project_id: data.projectId,
      name: data.name,
      color: data.color,
      description: data.description,
    });
    return transformLabel(response.data);
  },

  /**
   * Update a label
   */
  async update(labelId: string, data: UpdateLabelRequest): Promise<Label> {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.color !== undefined) payload.color = data.color;
    if (data.description !== undefined) payload.description = data.description;

    const response = await apiClient.patch<any>(`/labels/${labelId}`, payload);
    return transformLabel(response.data);
  },

  /**
   * Delete a label
   */
  async delete(labelId: string): Promise<void> {
    await apiClient.delete(`/labels/${labelId}`);
  },

  /**
   * Get label usage count
   */
  async getUsage(labelId: string): Promise<{ taskCount: number }> {
    const response = await apiClient.get<any>(`/labels/${labelId}/usage`);
    return { taskCount: response.data.task_count };
  },
};

// Transform functions
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformTask(data: any): Task {
  const task: Task = {
    id: data.id,
    boardId: data.boardId ?? data.board_id,
    columnId: data.columnId ?? data.column_id,
    taskKey: data.taskKey ?? data.task_key,
    taskNumber: data.taskNumber ?? data.task_number,
    title: data.title,
    description: data.description,
    taskType: data.taskType ?? data.task_type,
    priority: data.priority,
    position: data.position,
    assigneeId: data.assigneeId ?? data.assignee_id,
    assigneeContactId: data.assigneeContactId ?? data.assignee_contact_id,
    assigneeType: data.assigneeType ?? data.assignee_type,
    reporterId: data.reporterId ?? data.reporter_id,
    parentTaskId: data.parentTaskId ?? data.parent_task_id,
    estimatedMinutes: data.estimatedTime ?? data.estimated_time,
    actualMinutes: data.loggedTime ?? data.logged_time ?? 0,
    dueDate: data.dueDate ?? data.due_date,
    startDate: data.startDate ?? data.start_date,
    completedAt: data.completedAt ?? data.completed_at,
    labels: (data.labels || []).map(transformLabel),
    isCompleted: data.isCompleted ?? data.is_completed ?? false,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
    // Enriched fields
    subtaskCount: data.subtaskCount ?? data.subtask_count,
    subtaskCompletedCount: data.subtaskCompletedCount ?? data.subtask_completed_count,
    attachmentCount: data.attachmentCount ?? data.attachment_count,
    // Source meeting info
    sourceMeetingId: data.sourceMeetingId ?? data.source_meeting_id,
    // Source email info
    sourceEmailId: data.sourceEmailId ?? data.source_email_id,
    // Source language
    sourceLanguage: data.sourceLanguage ?? data.source_language,
  };

  // Add assignee object if present
  if (data.assignee) {
    task.assignee = {
      id: data.assignee.id,
      displayName: data.assignee.displayName ?? data.assignee.display_name,
      avatarUrl: data.assignee.avatarUrl ?? data.assignee.avatar_url,
      assigneeType: data.assignee.assigneeType ?? data.assignee.assignee_type ?? 'user',
      email: data.assignee.email,
      company: data.assignee.company,
    };
  }

  // Add source meeting object if present
  const sourceMeeting = data.sourceMeeting ?? data.source_meeting;
  if (sourceMeeting) {
    task.sourceMeeting = {
      id: sourceMeeting.id,
      title: sourceMeeting.title,
    };
  }

  // Add source email object if present
  const sourceEmail = data.sourceEmail ?? data.source_email;
  if (sourceEmail) {
    task.sourceEmail = {
      id: sourceEmail.id,
      subject: sourceEmail.subject,
    };
  }

  return task;
}

function transformTaskDetail(data: any): TaskDetail {
  const base = transformTask(data);
  const result: TaskDetail = {
    ...base,
    subtasks: (data.subtasks || []).map(transformSubtask),
    dependencies: (data.dependencies || []).map(transformDependency),
    customFieldValues: (data.customFieldValues ?? data.custom_field_values ?? []).map(
      transformCustomFieldValue
    ),
  };

  if (data.assignee) {
    result.assignee = {
      id: data.assignee.id,
      displayName: data.assignee.displayName ?? data.assignee.display_name,
      avatarUrl: data.assignee.avatarUrl ?? data.assignee.avatar_url,
      assigneeType: data.assignee.assigneeType ?? data.assignee.assignee_type ?? 'user',
      email: data.assignee.email,
      company: data.assignee.company,
    };
  }

  if (data.reporter) {
    result.reporter = {
      id: data.reporter.id,
      displayName: data.reporter.displayName ?? data.reporter.display_name,
    };
    const avatarUrl = data.reporter.avatarUrl ?? data.reporter.avatar_url;
    if (avatarUrl) {
      result.reporter.avatarUrl = avatarUrl;
    }
  }

  return result;
}

function transformSubtask(data: any): Subtask {
  return {
    id: data.id,
    taskId: data.taskId ?? data.task_id,
    title: data.title,
    isCompleted: data.isCompleted ?? data.is_completed,
    position: data.position,
    completedAt: data.completedAt ?? data.completed_at,
    completedById: data.completedById ?? data.completed_by_id,
    createdAt: data.createdAt ?? data.created_at,
  };
}

function transformLabel(data: any): Label {
  return {
    id: data.id,
    projectId: data.projectId ?? data.project_id,
    name: data.name,
    color: data.color,
    description: data.description,
  };
}

function transformDependency(data: any): TaskDependency {
  return {
    id: data.id,
    blockingTaskId: data.blockingTaskId ?? data.blocking_task_id,
    blockedTaskId: data.blockedTaskId ?? data.blocked_task_id,
    dependencyType: data.dependencyType ?? data.dependency_type,
    createdAt: data.createdAt ?? data.created_at,
  };
}

function transformCustomFieldValue(data: any): CustomFieldValue {
  return {
    fieldId: data.fieldId ?? data.field_id,
    fieldName: data.fieldName ?? data.field_name,
    fieldType: data.fieldType ?? data.field_type,
    value: data.value,
  };
}

export default tasksApi;
